/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import {useMemo} from 'react'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getPathWithLocale} from '../utils/url'
import {useLocation} from 'react-router-dom'
import useGetSSRCustomInfo from './use-get-ssr-custom-info'
import {isServer} from '../utils/utils'

const useMulticountryUtils = () => {
    const {locale, site, buildUrl} = useMultiSite()
    const location = useLocation()
    const {data} = useGetSSRCustomInfo()

    /**
     * Build the path for the current locale
     * @returns {string} - The localized path.
     */
    const buildLocalizedUrl = (pathname, localeId = null) => {
        const location = {
            pathname,
            search: '',
        }
        return getPathWithLocale(localeId || locale.id, buildUrl, {
            location,
        })
    }

    /**
     * Get language codes
     */
    const getLanguageCodes = () => {
        return site?.l10n.supportedLocales.map((locale) => locale.alias) || []
    }

    /**
     * Memoized language codes
     */
    const languageCodes = useMemo(() => getLanguageCodes(), [locale.id])

    /**
     * Current language in URL is enabled
     */
    const localeIsPresentInUrl = () => {
        const parts = location.pathname.split('/')
        return languageCodes.includes(parts?.[1])
    }

    const isBotClientside =
        !isServer && /bot|crawl|slurp|spider|mediapartners/i.test(window?.navigator?.userAgent)

    const isSplashPage = (data?.isBot || isBotClientside) && !localeIsPresentInUrl()

    return {
        buildLocalizedUrl,
        localeIsPresentInUrl,
        isSplashPage,
    }
}

export default useMulticountryUtils
